import {
  useConsultationForClientQuery,
  useCreateConsulterMessageMutation,
  CreateConsulterMessageInput,
  MessageStatus,
} from '@/graphql/types'
import {
  Button,
  Textarea,
  ErrorMessage,
  LinkButton,
  Card,
} from '@/react/client/components/Basics'
import { AboutConsultation } from '@/react/shared/components/AboutCosultation'
import { AuthButtons } from '@/react/shared/components/AuthButtons'
import { Modal } from '@/react/shared/components/Modal'
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../components/AuthProvider'
import { BluredDoctorMessage } from '../../components/BluredDoctorMessage'
import { ConsultationCard } from '../../components/ConsultationCard'
import MessageCard from '../../components/MessageCard'
import NotFound from '../../components/NotFound'

export const Show: React.FC = () => {
  const [modal, setModal] = useState<boolean>(false)
  const { id } = useParams<{ id: string }>()
  const {
    data,
    loading: loadingConsultation,
    refetch,
  } = useConsultationForClientQuery({
    variables: { id },
  })
  const [input, setInput] = useState<CreateConsulterMessageInput>({
    body: '',
    status: MessageStatus.Checked,
    consultationId: id,
  })
  const [reply, setReply] = useState<boolean>(false)
  const [submittable, setSubmittable] = useState<boolean>(false)

  const {
    loading: loadingCurrentConsulter,
    currentConsulter,
    isLoggedIn,
  } = useAuth()

  const loading = loadingCurrentConsulter || loadingConsultation

  const isOwnConsultation = !!(
    currentConsulter?.progressConsultation &&
    currentConsulter.progressConsultation.id === id
  )

  const checkInputValidation = (str: string): string => {
    if (str.length > 1000) {
      return '相談内容は1000字未満である必要があります。'
    } else if (str.length === 0) {
      return '相談内容が未入力です。'
    } else {
      return ''
    }
  }

  useEffect(() => {
    if (checkInputValidation(input.body).length === 0) {
      setSubmittable(true)
    } else {
      setSubmittable(false)
    }
  }, [input])

  useEffect(() => {
    const nodes = data?.consultation?.displayMessagesForConsulter?.nodes
    if (!nodes || nodes.length === 0) {
      setReply(false)
      return
    }

    const length = nodes.length
    const latestMessage = nodes[length - 1]!
    // 最新のメッセージが医師のメッセージでかつ既読の場合、または管理者のメッセージの場合は返信可能
    if (
      (latestMessage.doctorMessage && latestMessage.status === 'checked') ||
      latestMessage.adminMessage
    ) {
      setReply(true)
    } else {
      setReply(false)
    }
  }, [data?.consultation?.displayMessagesForConsulter])

  const [mutate, { error }] = useCreateConsulterMessageMutation()

  const bottomElement = () => {
    if (loading) return
    // 未ログインの場合は会員登録導線
    if (!isLoggedIn) {
      return (
        <div className="pb-10">
          <div className="pc:w-pc-inner-xs mx-auto px-4 pc:px-0">
            <AuthButtons dataMnCspLocation="本文中_01" />
          </div>
          <div className="mt-10">
            <AboutConsultation />
          </div>
          <div className="px-4 pc:px-0 pb-10 bg-base-light-color">
            <div className="pc:w-pc-inner-xs mx-auto">
              <AuthButtons dataMnCspLocation="本文中_02" />
            </div>
          </div>
        </div>
      )
    }

    // ログイン済みでかつ自分の相談でない場合は相談画面への導線
    if (isLoggedIn && !isOwnConsultation) {
      return (
        <div className="pc:w-pc-inner-xs pc:mx-auto mx-4">
          <div className="flex justify-center pb-10">
            <LinkButton
              className="w-11/12 mx-auto focus:outline-none focus:ring focus:border-blue-300  bg-link-color hover:bg-link-color"
              href="/features/qa/mild_cognitive_impairment/consultations"
            >
              医師に相談する
            </LinkButton>
          </div>
        </div>
      )
    }

    // 自分の相談かつ返信可能な場合は返信ボタン
    if (isOwnConsultation && reply) {
      return (
        <div className="pc:w-pc-inner-xs mx-auto mb-10 px-4 pc:px-0 flex justify-center">
          <Button
            className="w-11/12 bg-link-color hover:bg-link-color"
            disabled={!submittable}
            onClick={() => {
              setModal(true)
            }}
          >
            相談を追加する
          </Button>
        </div>
      )
    }
  }

  const consultationCard = () => {
    if (data?.consultation) {
      return (
        <div className="pc:w-pc-inner-sm mx-auto">
          <ConsultationCard
            key={data.consultation.id}
            consultation={data.consultation}
          />
        </div>
      )
    } else {
      return
    }
  }

  const logInUserElement = () => {
    return (
      <>
        <div className="px-4 py-6 bg-gray-10 mx-auto">{consultationCard()}</div>
        {middleElement()}
        {data?.consultation?.displayMessagesForConsulter?.nodes?.length ? (
          <div className="mt-8 px-4 py-6 bg-gray-10">
            <div className="pc:w-pc-inner-sm mx-auto flex flex-col gap-6">
              {data?.consultation?.displayMessagesForConsulter?.nodes?.map(
                (message) => {
                  return <MessageCard message={message!} key={message!.id} />
                }
              )}
            </div>
            {isOwnConsultation && reply && (
              <>
                <div className="pc:w-pc-inner-sm mx-auto">
                  <div className="mt-10">
                    <h2 className="font-bold text-lg text-app-color">
                      相談を追加する
                    </h2>
                    <Card className="mt-4">
                      <p className="font-bold text-lg">
                        本文 <span className="font-normal">（21〜1000字）</span>
                      </p>
                      <Textarea
                        placeholder="本文を入力"
                        className="mt-2 h-48 w-full py-3 px-4 focus:outline-none focus:ring focus:border-blue-300 border border-gray-50 rounded focus:ring-2 focus:ring-blue-600"
                        onChange={(e) =>
                          setInput({ ...input, body: e.target.value })
                        }
                      ></Textarea>
                      <p className="text-red-500 text-sm font-bold">
                        {input.body.length !== 0 &&
                          checkInputValidation(input.body)}
                      </p>
                      <p className="mt-1 text-xxs">
                        ※症状は、いつから・どこが・どのような状態なのか、何を知りたいのか、具体的に入力してください。
                        <br />
                        ※お名前や住所など個人情報の入力はしないようにお願いいたします。
                      </p>
                      <ErrorMessage error={error} columnName="title" />
                    </Card>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          ''
        )}
      </>
    )
  }

  const anonymousUserElement = () => {
    if (loading) return
    return (
      <>
        <div className="px-4 py-6 bg-gray-10">{consultationCard()}</div>
        {middleElement()}
        {data?.consultation?.displayMessagesForConsulter?.nodes?.[0]?.body && (
          <div className="mt-8">
            <BluredDoctorMessage
              body={
                data?.consultation?.displayMessagesForConsulter?.nodes?.[0]
                  ?.body
              }
            />
          </div>
        )}
      </>
    )
  }

  const middleElement = () => {
    return (
      <div className="pt-8">
        <div className="py-2 px-4 bg-sub-color font-bold text-lg text-center text-app-color">
          各回答は、回答日時点での情報です。
          <br />
          情報はログインすることで
          <br className="pc:hidden" />
          ご確認いただけます。
        </div>
      </div>
    )
  }

  const cautionElement = () => {
    if (loading) return
    return (
      <div className="pc:w-pc-inner-xs mx-auto py-8 px-4 pc:px-0">
        <p className="text-xxs text-gray-70">
          本サービスは医師による健康相談サービスで、医師による回答は相談内容に応じた医学的助言です。診断・診察などを行うものではありません。
          このことを十分認識したうえで自己の責任において、医療機関への受診有無等をご自身でご判断ください。
          実際に医療機関を受診する際も、治療方法、薬の内容等、担当の医師によく相談、確認するようにお願いいたします。
        </p>
      </div>
    )
  }

  const mainElement = () => {
    if (loading) return
    if (isLoggedIn) {
      return logInUserElement()
    } else {
      return anonymousUserElement()
    }
  }

  const submit = () => {
    mutate({
      variables: {
        input: {
          ...input,
        },
      },
      update() {
        refetch()
        setModal(false)
      },
    })
  }
  return (
    <div>
      <div className="w-full mx-auto min-h-sp-screen pc:min-h-pc-screen">
        <div className="pc:w-pc-inner-sm mx-auto">
          <h1 className="text-app-color font-bold py-8 px-4 pc:px-0 text-2xl">
            相談詳細
          </h1>
        </div>
        {data?.consultation === null ? (
          <NotFound />
        ) : (
          <>
            {mainElement()}
            {cautionElement()}
            {bottomElement()}
          </>
        )}
      </div>
      <Modal
        title="投稿後は編集、削除できません"
        isOpen={modal}
        onRequestClose={() => setModal(false)}
      >
        <div className="p-5 text-bold text-lg">
          一度投稿した内容は編集・削除することができなくなります。
          <br />
          投稿後は、先生の回答があるまで追加投稿できませんので、よく確認をしてから投稿をしてください。
        </div>
        <div className="flex justify-center w-full">
          <Button
            className="w-11/12 bg-link-color hover:bg-link-color"
            onClick={submit}
          >
            投稿する
          </Button>
        </div>
        <div className="flex justify-center w-full">
          <button
            className="mt-3 text-link-color border-blue-500 underline"
            onClick={() => setModal(false)}
          >
            キャンセル
          </button>
        </div>
      </Modal>
    </div>
  )
}
